"use client";

import { useQuery } from "@tanstack/react-query";
import { apiClient, get } from "api";
import { fmt, parse } from "visuais";
import type {
  CondicaoPagamentoParams,
  CondicaoPagamentoResponse,
  ParcelaPayload,
  DateISO,
} from "@PN/inclusao";

// Utilitário para converter a data BR (dd/MM/yyyy) em ISO (yyyy-MM-dd)
const toDateISO = (dateStr: string | null | undefined): DateISO => {
  if (!dateStr) return "" as DateISO;
  // Se vier no formato dd/MM/yyyy, converte corretamente
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [dd, mm, yyyy] = dateStr.split("/").map(Number);
    if (!dd || !mm || !yyyy || isNaN(dd) || isNaN(mm) || isNaN(yyyy))
      return "" as DateISO;
    return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(
      2,
      "0"
    )}` as DateISO;
  }
  // Fallback para parse.dates.BR se necessário
  const date = parse.dates.BR(dateStr, { allowNull: true });
  if (!date) return "" as DateISO;
  return date.toISOString().slice(0, 10) as DateISO;
};

/** Soma um dia à data ISO (yyyy-MM-dd). */
const addOneDay = (isoDate: DateISO): DateISO => {
  if (!isoDate) return "" as DateISO;
  const d = new Date(isoDate + "T12:00:00");
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10) as DateISO;
};

/** Se a data cair em domingo, desloca para segunda-feira. */
const moveSundayToMonday = (isoDate: DateISO): DateISO => {
  if (!isoDate) return "" as DateISO;
  const d = new Date(isoDate + "T12:00:00");
  if (d.getDay() !== 0) return isoDate;
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10) as DateISO;
};

const fixTrailingCommas = (txt: string) =>
  txt.replace(/,\s*}/g, "}").replace(/,\s*]/g, "]");

/** Condições que não recebem +1 dia no vencimento (demais condições recebem) */
const CONDICOES_SEM_UM_DIA = ["C01", "C35"];

const ENDPOINT = "reidoapsdu/condicaov2";

/**
 * Função para buscar parcelas da API de condição de pagamento.
 * A lógica de tenantId e refresh de token é gerenciada automaticamente pela API base.
 */
export async function fetchParcelas(
  params: CondicaoPagamentoParams
): Promise<ParcelaPayload[]> {
  const { valor, condpag, dtemissao, codForn, lojaForn } = params;
  const condicaoCodigo = (condpag || "").trim().toUpperCase();

  try {
    const { data: raw } = await get<unknown>(apiClient.production, ENDPOINT, {
      headers: {
        valor: String(valor),
        condpag,
        dtemissao: fmt.dates.BR(dtemissao),
        codForn,
        lojaForn,
      },
    });

    let response: CondicaoPagamentoResponse;
    if (typeof raw === "string") {
      try {
        response = JSON.parse(raw) as CondicaoPagamentoResponse;
      } catch {
        response = JSON.parse(
          fixTrailingCommas(raw)
        ) as CondicaoPagamentoResponse;
      }
    } else {
      response = raw as CondicaoPagamentoResponse;
    }
    
    // Converte para ParcelaPayload; vencimento +1 dia para todas, exceto C01 e C35
    const aplicaUmDia = !CONDICOES_SEM_UM_DIA.includes(condicaoCodigo);
    return (
      response.Pagamentos?.map((p, i) => {
        const venc = toDateISO(p.Vencimento);
        const vencAjustadoPorCondicao = aplicaUmDia ? addOneDay(venc) : venc;
        return {
          item: p.Parcela ?? String(i + 1),
          vencimento:
            condicaoCodigo === "C01"
              ? moveSundayToMonday(vencAjustadoPorCondicao)
              : vencAjustadoPorCondicao,
          valor: p.Valor ?? 0,
        };
      }) || []
    );
  } catch (error: any) {
    console.error("❌ [Parcelas] Erro ao buscar parcelas:", error);
    throw error;
  }
}

/**
 * Hook React para buscar parcelas usando TanStack Query.
 */
export function useParcelasQuery(
  params: CondicaoPagamentoParams,
  enabled = true
) {
  return useQuery({
    queryKey: ["parcelas", params],
    queryFn: () => fetchParcelas(params),
    enabled,
    // Configurações para controle manual de refetch
    staleTime: Infinity, // Nunca considera stale automaticamente
    gcTime: 5 * 60 * 1000, // Cache de 5 minutos
    // Refetch controlado manualmente
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Retry automático em caso de erro (a API base já trata auth)
    retry: (failureCount, error: any) => {
      // Para erros de auth (401/403), a API base já trata automaticamente
      if (error.response?.status === 401 || error.response?.status === 403) {
        return false;
      }
      // Para outros erros, tenta até 2 vezes
      return failureCount < 2;
    },
  });
}
